/**
 * VariableNameField - Dropdown populated with variable names from InitializeVariable actions.
 * Wraps ComboboxField with options sourced from the current workflow.
 * Optionally filters by variable type using supportedTypes from editor options.
 */

import { useMemo } from 'react';
import { ComboboxField } from '../fields/ComboboxField';
import { useAvailableVariables } from '@/hooks/useAvailableVariables';
import type { ValueSegment } from '@/types/valueSegment';

interface VariableNameFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  hasRunData?: boolean | undefined;
  isStale?: boolean | undefined;
  /** From x-ms-editor-options — e.g. { supportedTypes: ['integer', 'float'] } */
  editorOptions?: Record<string, unknown> | undefined;
}

/** Normalize variable type names for comparison (case-insensitive) */
function normalizeType(t: string): string {
  return t.toLowerCase();
}

export const VariableNameField = ({
  name,
  label,
  value,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
  hasRunData,
  isStale,
  editorOptions,
}: VariableNameFieldProps) => {
  const allVariables = useAvailableVariables();
  const supportedTypes = editorOptions?.supportedTypes as string[] | undefined;

  const options = useMemo(() => {
    let filtered = allVariables;
    if (supportedTypes && supportedTypes.length > 0) {
      const allowed = new Set(supportedTypes.map(normalizeType));
      filtered = allVariables.filter((v) => allowed.has(normalizeType(v.type)));
    }
    return filtered.map((v) => ({
      label: v.name,
      value: v.name,
    }));
  }, [allVariables, supportedTypes]);

  return (
    <ComboboxField
      name={name}
      label={label}
      value={value}
      // This wrapper is single-select only (never passes `multiSelect`), so
      // ComboboxField can never emit a `string[]` here. `onChange` stays typed
      // as single-select and we cast at the boundary to satisfy ComboboxField's
      // wider (multi-select-capable) signature.
      onChange={onChange as (value: string | string[] | ValueSegment[]) => void}
      options={options}
      description={description}
      placeholder={options.length > 0 ? (placeholder ?? 'Select a variable') : 'No variables defined — add an Initialize Variable action first'}
      required={required}
      disabled={disabled}
      error={error}
      hasRunData={hasRunData}
      isStale={isStale}
    />
  );
};
